import { useCallback } from 'react';

/**
 * Hook for making authenticated requests to the TikTok Shop backend API.
 */
export function useTikTokFetch() {
  const getShopId = (): string | null => {
    // Get shop_id from URL params or session storage
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('shop') || sessionStorage.getItem('tiktok_shop_id');
  };

  const fetchWithAuth = useCallback(
    async (url: string, options: RequestInit = {}): Promise<Response> => {
      const apiUrl = url.startsWith('/') ? url : `/${url}`;

      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        ...(options.headers as Record<string, string>),
      };

      return fetch(apiUrl, {
        ...options,
        headers,
      });
    },
    []
  );

  const get = useCallback(
    async <T>(url: string): Promise<T> => {
      const response = await fetchWithAuth(url);
      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.detail || 'Request failed');
      }
      return response.json();
    },
    [fetchWithAuth]
  );

  const post = useCallback(
    async <T>(url: string, data?: unknown): Promise<T> => {
      const response = await fetchWithAuth(url, {
        method: 'POST',
        body: data ? JSON.stringify(data) : undefined,
      });
      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.detail || 'Request failed');
      }
      return response.json();
    },
    [fetchWithAuth]
  );

  const put = useCallback(
    async <T>(url: string, data?: unknown): Promise<T> => {
      const response = await fetchWithAuth(url, {
        method: 'PUT',
        body: data ? JSON.stringify(data) : undefined,
      });
      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.detail || 'Request failed');
      }
      return response.json();
    },
    [fetchWithAuth]
  );

  const del = useCallback(
    async <T>(url: string): Promise<T> => {
      const response = await fetchWithAuth(url, {
        method: 'DELETE',
      });
      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.detail || 'Request failed');
      }
      return response.json();
    },
    [fetchWithAuth]
  );

  return { fetch: fetchWithAuth, get, post, put, del, getShopId };
}

// Type definitions for API responses
export interface ShopInfo {
  id: string;
  shop_id: string;
  shop_name: string | null;
  region: string | null;
  is_active: boolean;
  is_verified: boolean;
  brand_id: string | null;
  total_products: number;
  total_orders: number;
  total_creators: number;
  total_revenue: number;
  commission_rate: number;
  last_sync: string | null;
  created_at: string | null;
}

export interface Product {
  id: string;
  tiktok_product_id: string;
  title: string;
  price: number | null;
  currency: string;
  main_image_url: string | null;
  status: string;
  is_in_stock: boolean;
  video_count: number;
  total_views: number;
  total_sales: number;
  synced_at: string | null;
}

export interface Creator {
  id: string;
  creator_id: string;
  tiktok_username: string | null;
  display_name: string | null;
  avatar_url: string | null;
  follower_count: number;
  status: string;
  is_active: boolean;
  commission_rate: number;
  total_orders: number;
  total_revenue: number;
  total_commission: number;
  conversion_rate: number;
  created_at?: string | null;
}

export interface Order {
  id: string;
  tiktok_order_id: string;
  order_status: string;
  payment_status: string;
  product_title: string | null;
  quantity: number;
  total_amount: number | null;
  currency: string;
  commission_amount: number;
  attribution_source: string | null;
  creator_id: string | null;
  order_created_at: string | null;
}

export interface AnalyticsOverview {
  total_revenue: number;
  total_orders: number;
  total_commission: number;
  total_creators: number;
  total_products: number;
  revenue_trend: number;
  orders_trend: number;
  top_products: Array<{
    id: string;
    title: string;
    sales: number;
    views: number;
  }>;
  top_creators: Array<{
    id: string;
    name: string;
    revenue: number;
    orders: number;
  }>;
  recent_orders: Array<{
    id: string;
    order_id: string;
    status: string;
    amount: number | null;
    created_at: string | null;
  }>;
}

// Helper functions
export function formatCurrency(cents: number, currency = 'USD'): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
  }).format(cents / 100);
}

export function formatNumber(num: number): string {
  if (num >= 1000000) {
    return (num / 1000000).toFixed(1) + 'M';
  }
  if (num >= 1000) {
    return (num / 1000).toFixed(1) + 'K';
  }
  return num.toString();
}

export function formatPercentage(value: number): string {
  return `${value >= 0 ? '+' : ''}${value.toFixed(1)}%`;
}

export function formatDate(dateString: string | null): string {
  if (!dateString) return 'Never';
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}
